import {
  BarChart3,
  FileText,
  FolderTree,
  LayoutDashboard,
  Network,
  Package,
  Settings,
  ShoppingCart,
  Trophy,
  Truck,
  UserCheck,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";

/**
 * Admin sidebar menu config.
 *
 * - A section groups related top-level items under a heading.
 * - An item with `url` is a link; an item with `children` is a collapsible submenu.
 * - Submenus nest to any depth: add `children` to any child to create a sub-submenu.
 * - Top-level items need an `icon`; nested items don't.
 */
export type NavBadgeKey = "stores" | "products";

export interface NavItem {
  title: string;
  url?: string;
  icon?: LucideIcon;
  badgeKey?: NavBadgeKey;
  /** Match the URL exactly (not as a prefix) when highlighting. */
  end?: boolean;
  /** Shortcut to a page that already lives elsewhere in the menu: never highlighted or used to auto-open its parent. */
  alias?: boolean;
  children?: NavItem[];
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

export const adminNav: NavSection[] = [
  {
    id: "main",
    label: "Main",
    items: [
      { title: "Dashboard", url: "/admin", icon: LayoutDashboard, end: true },
    ],
  },
  {
    id: "commerce",
    label: "Commerce",
    items: [
      {
        title: "Manage Products",
        icon: Package,
        children: [
          { title: "Store products", url: "/admin/products", badgeKey: "products" },
          { title: "Channel products", url: "/admin/channel-products" },
        ],
      },
      { title: "Categories", url: "/admin/categories", icon: FolderTree },
      {
        title: "Manage Orders",
        icon: ShoppingCart,
        children: [
          { title: "Store orders", url: "/admin/orders" },
          { title: "Channel orders", url: "/admin/channel-orders" },
        ],
      },
    ],
  },
  {
    id: "management",
    label: "Manage",
    items: [
      { title: "Users", url: "/admin/users", icon: Users },
      { title: "KYC Approvals", url: "/admin/kyc", icon: UserCheck },
      { title: "Delivery", url: "/admin/delivery", icon: Truck },
      {
        title: "Distribution",
        icon: Network,
        children: [
          { title: "Distributors", url: "/admin/distributors" },
          { title: "Wholesalers", url: "/admin/wholesalers" },
          // Stores are the retailers, so this is the store management page (keeps the pending-approval badge).
          { title: "Retailers", url: "/admin/stores", badgeKey: "stores" },
        ],
      },
      {
        title: "Rewards",
        icon: Trophy,
        children: [
          { title: "Rank Rewards", url: "/admin/rank-rewards" },
          { title: "Completed Rewards", url: "/admin/completed-rewards" },
        ],
      },
    ],
  },
  {
    id: "content",
    label: "Content",
    items: [
      {
        title: "CMS",
        icon: FileText,
        children: [
          { title: "CMS Pages", url: "/admin/cms" },
          { title: "Banners", url: "/admin/banners" },
          { title: "Coupons", url: "/admin/coupons" },
        ],
      },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    items: [
      {
        title: "Wallet",
        icon: Wallet,
        children: [
          { title: "Payouts & Adjust", url: "/admin/wallet", end: true },
          { title: "Transactions", url: "/admin/wallet/transactions" },
          { title: "Commission Distribution", url: "/admin/income/distribution" },
        ],
      },
      { title: "Reports", url: "/admin/reports", icon: BarChart3 },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    items: [
      { title: "Settings", url: "/admin/settings", icon: Settings },
    ],
  },
];

export function isRouteActive(pathname: string, url: string) {
  if (url === "/admin") return pathname === "/admin";
  return pathname === url || pathname.startsWith(`${url}/`);
}

/** True when the item, or any descendant, matches the current route. */
export function isItemActive(pathname: string, item: NavItem): boolean {
  if (item.url && !item.alias && isRouteActive(pathname, item.url)) return true;
  return item.children?.some((child) => isItemActive(pathname, child)) ?? false;
}

/** Paths ("Distribution", "Distribution/Channel", …) of every submenu that should be open for a route. */
export function activeGroupPaths(pathname: string, items: NavItem[], parent = ""): string[] {
  return items.flatMap((item) => {
    if (!item.children) return [];
    const path = parent ? `${parent}/${item.title}` : item.title;
    return isItemActive(pathname, item) ? [path, ...activeGroupPaths(pathname, item.children, path)] : [];
  });
}
